/**
 * @file 첨부파일 미리보기 모달 (풀스크린, 각진 테두리)
 *  - 이미지 → <img>
 *  - 엑셀(xlsx/xlsm) → ExcelJS 로 값+스타일(색상·글꼴·정렬)·병합·열너비·파일 틀고정까지 그대로 렌더
 *    (xls/ods/csv 는 SheetJS 폴백 — 값만). 시트 탭·확대축소·셀 드래그 선택 지원.
 *  - 그 외(PDF·오피스 변환 PDF·텍스트) → <iframe>
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Box, Typography, CircularProgress, IconButton } from "@mui/material";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { mailApi } from "../api/mailApi";
import { ds } from "../../../styles/designSystem";

const C = ds.colors;

const isImage = (att) =>
    (att?.mimeType || "").startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(att?.filename || "");

const isSpreadsheet = (att) =>
    /\.(xlsx|xls|ods|csv)$/i.test(att?.filename || "") ||
    (att?.mimeType || "").includes("spreadsheet") ||
    (att?.mimeType || "").includes("ms-excel") ||
    (att?.mimeType || "").includes("csv");

const MAX_ROWS = 800;
const MAX_COLS = 50;
const ROW_H = 24;
const HEAD_H = 26;
const ROWNUM_W = 48;
const DEFAULT_COL_W = 96;
const MIN_COL_W = 54;

const fmtNum = (n) => (n == null ? "" : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));
const argbToHex = (argb) => {
    if (!argb || typeof argb !== "string") return null;
    const s = argb.length === 8 ? argb.slice(2) : argb;   // "FFRRGGBB" → "RRGGBB"
    return /^[0-9a-fA-F]{6}$/.test(s) ? `#${s}` : null;
};

/** ExcelJS 셀 → 정규화 셀 { t, num, fill, color, bold, align } */
function readExcelCell(cell) {
    let text = "", num = null;
    const val = cell.value;
    if (val == null) text = "";
    else if (typeof val === "number") { num = val; text = cell.text ?? String(val); }
    else if (typeof val === "object") {
        if (typeof val.result === "number") { num = val.result; text = cell.text ?? String(val.result); }
        else text = cell.text ?? "";
    } else text = cell.text ?? String(val);
    const out = { t: text, num };
    const f = cell.fill;
    if (f && f.type === "pattern" && f.pattern === "solid" && f.fgColor) {
        const hex = argbToHex(f.fgColor.argb);
        if (hex) out.fill = hex;
    }
    const font = cell.font;
    if (font) {
        const c = font.color && argbToHex(font.color.argb);
        if (c) out.color = c;
        if (font.bold) out.bold = true;
    }
    if (cell.alignment && cell.alignment.horizontal) out.align = cell.alignment.horizontal;
    return out;
}

/** ExcelJS worksheet → 정규화 시트 모델 */
function buildExcelSheet(ws) {
    const nRowsAll = ws.rowCount || 0, nColsAll = ws.columnCount || 0;
    const nRows = Math.min(MAX_ROWS, nRowsAll), nCols = Math.min(MAX_COLS, nColsAll);
    const cells = [];
    for (let r = 1; r <= nRows; r++) {
        const row = ws.getRow(r);
        const arr = [];
        for (let c = 1; c <= nCols; c++) arr.push(readExcelCell(row.getCell(c)));
        cells.push(arr);
    }
    const covered = new Set(), spanAt = new Map();
    ((ws.model && ws.model.merges) || []).forEach((rng) => {
        try {
            const { s, e } = XLSX.utils.decode_range(rng);
            if (s.r >= nRows || s.c >= nCols) return;
            const er = Math.min(e.r, nRows - 1), ec = Math.min(e.c, nCols - 1);
            spanAt.set(`${s.r}:${s.c}`, { rs: er - s.r + 1, cs: ec - s.c + 1 });
            for (let rr = s.r; rr <= er; rr++)
                for (let cc = s.c; cc <= ec; cc++)
                    if (!(rr === s.r && cc === s.c)) covered.add(`${rr}:${cc}`);
        } catch { /* skip bad merge */ }
    });
    const colW = [];
    for (let c = 1; c <= nCols; c++) {
        const w = ws.getColumn(c).width;
        colW.push(Math.max(MIN_COL_W, Math.min(360, Math.round(w ? w * 7 + 8 : DEFAULT_COL_W))));
    }
    const v = (ws.views || []).find((x) => x.state === "frozen");
    const freeze = v ? { r: Math.min(v.ySplit || 0, nRows), c: Math.min(v.xSplit || 0, nCols) } : { r: 0, c: 0 };
    return { nRows, nCols, cells, covered, spanAt, colW, freeze, truncated: nRowsAll > nRows || nColsAll > nCols };
}

/** SheetJS worksheet → 정규화 시트 모델 (값만, 스타일·틀고정 없음) */
function buildSheetJsSheet(ws) {
    if (!ws || !ws["!ref"]) return { nRows: 0, nCols: 0, cells: [], covered: new Set(), spanAt: new Map(), colW: [], freeze: { r: 0, c: 0 }, truncated: false };
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const nRowsAll = range.e.r + 1, nColsAll = range.e.c + 1;
    const nRows = Math.min(MAX_ROWS, nRowsAll), nCols = Math.min(MAX_COLS, nColsAll);
    const cells = [];
    for (let r = 0; r < nRows; r++) {
        const arr = [];
        for (let c = 0; c < nCols; c++) {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            const t = cell ? (cell.w != null ? cell.w : cell.v) : "";
            arr.push({ t: t == null ? "" : String(t), num: cell && typeof cell.v === "number" ? cell.v : null });
        }
        cells.push(arr);
    }
    const covered = new Set(), spanAt = new Map();
    (ws["!merges"] || []).forEach((m) => {
        if (m.s.r >= nRows || m.s.c >= nCols) return;
        const er = Math.min(m.e.r, nRows - 1), ec = Math.min(m.e.c, nCols - 1);
        spanAt.set(`${m.s.r}:${m.s.c}`, { rs: er - m.s.r + 1, cs: ec - m.s.c + 1 });
        for (let rr = m.s.r; rr <= er; rr++)
            for (let cc = m.s.c; cc <= ec; cc++)
                if (!(rr === m.s.r && cc === m.s.c)) covered.add(`${rr}:${cc}`);
    });
    const cols = ws["!cols"] || [];
    const colW = [];
    for (let c = 0; c < nCols; c++) {
        const cc = cols[c];
        const w = cc?.wpx ? cc.wpx : cc?.wch ? cc.wch * 7 + 10 : DEFAULT_COL_W;
        colW.push(Math.max(MIN_COL_W, Math.min(360, Math.round(w))));
    }
    return { nRows, nCols, cells, covered, spanAt, colW, freeze: { r: 0, c: 0 }, truncated: nRowsAll > nRows || nColsAll > nCols };
}

/** 정규화 시트 모델을 엑셀 느낌으로 렌더. zoom 은 상위(모달 헤더)에서 제어. */
function SpreadsheetView({ source, sheetIdx, setSheetIdx, zoom }) {
    const names = source.names;

    const sheet = useMemo(() => {
        if (source.kind === "excel") return buildExcelSheet(source.wb.worksheets[sheetIdx]);
        return buildSheetJsSheet(source.wb.Sheets[names[sheetIdx]]);
    }, [source, names, sheetIdx]);

    // 기본(무배율) 치수 — 확대는 표를 다시 그리지 않고 래퍼의 CSS zoom 으로만 처리(성능 핵심).
    const rowH = ROW_H;
    const headH = HEAD_H;
    const rownumW = ROWNUM_W;
    const fontSize = 12;
    const pad = "2px 8px";
    const colWz = sheet.colW;

    const cellStyle = (r, c) => {
        const cell = sheet.cells[r]?.[c] || {};
        const st = {
            boxSizing: "border-box",
            border: "1px solid #e2e8f0", padding: pad, height: rowH, fontSize,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            background: cell.fill || "#fff", color: cell.color || "#0f172a",
        };
        if (cell.bold) st.fontWeight = 700;
        if (cell.align) st.textAlign = cell.align;
        return st;
    };
    // 헤더 행/행번호 열은 스크롤 시 항상 보이도록 sticky (엑셀의 열문자·행번호 — 파일 틀고정과는 별개).
    // box-sizing:border-box — <col> 폭이 곧 렌더 폭이 되어 오버레이 좌표(colLeft)와 정확히 일치.
    const colHeadStyle = {
        boxSizing: "border-box",
        position: "sticky", top: 0, zIndex: 6, background: "#f1f5f9",
        border: "1px solid #e2e8f0", padding: pad, fontWeight: 600, fontSize,
        color: "#64748b", textAlign: "center", whiteSpace: "nowrap", height: headH,
    };
    const rowNumStyle = {
        boxSizing: "border-box",
        position: "sticky", left: 0, zIndex: 6, background: "#f1f5f9",
        border: "1px solid #e2e8f0", padding: pad, color: "#94a3b8", fontSize,
        textAlign: "center", fontWeight: 600, height: rowH,
    };
    const cornerStyle = { boxSizing: "border-box", position: "sticky", top: 0, left: 0, zIndex: 8, background: "#eef2f6", border: "1px solid #e2e8f0", height: headH };

    // 표는 선택과 무관하게 memo — 드래그 시 수만 셀 재렌더 방지(성능 핵심).
    const tableEl = useMemo(() => (
        <table style={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed", fontVariantNumeric: "tabular-nums", userSelect: "none" }}>
            <colgroup>
                <col style={{ width: rownumW }} />
                {Array.from({ length: sheet.nCols }, (_, c) => <col key={c} style={{ width: colWz[c] }} />)}
            </colgroup>
            <thead>
                <tr style={{ height: headH }}>
                    <th style={cornerStyle} />
                    {Array.from({ length: sheet.nCols }, (_, c) => (
                        <th key={c} style={colHeadStyle}>{XLSX.utils.encode_col(c)}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: sheet.nRows }, (_, r) => (
                    <tr key={r} style={{ height: rowH }}>
                        <th style={rowNumStyle}>{r + 1}</th>
                        {Array.from({ length: sheet.nCols }, (_, c) => {
                            if (sheet.covered.has(`${r}:${c}`)) return null;
                            const span = sheet.spanAt.get(`${r}:${c}`);
                            const text = sheet.cells[r]?.[c]?.t ?? "";
                            return (
                                <td key={c} data-r={r} data-c={c} rowSpan={span?.rs} colSpan={span?.cs}
                                    style={cellStyle(r, c)} title={text}>
                                    {text}
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [sheet]);

    return (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
            {/* 표. 확대는 래퍼의 CSS zoom 으로만 → 표는 재렌더 없이 즉시 확대. */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <div style={{ zoom, width: "max-content" }}>
                    {tableEl}
                </div>
            </Box>

            {sheet.truncated && (
                <Typography sx={{ px: 1.5, py: 0.4, fontSize: 11, color: C.textMuted, bgcolor: "#fbfcfe", borderTop: `1px solid ${C.border}`, flexShrink: 0, whiteSpace: "nowrap" }}>
                    일부만 표시 (최대 {MAX_ROWS}행·{MAX_COLS}열) · 전체는 다운로드해 확인하세요.
                </Typography>
            )}

            {/* 하단 시트 탭 */}
            <Box sx={{ display: "flex", px: 1, borderTop: `1px solid ${C.border}`, bgcolor: "#f8fafc", overflowX: "auto", flexShrink: 0 }}>
                {names.map((name, i) => (
                    <Box key={i} onClick={() => setSheetIdx(i)}
                        sx={{
                            px: 1.75, py: 0.7, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap",
                            bgcolor: i === sheetIdx ? "#fff" : "transparent",
                            borderTop: i === sheetIdx ? `2px solid ${C.primary}` : "2px solid transparent",
                            fontWeight: i === sheetIdx ? 700 : 500,
                            color: i === sheetIdx ? C.primary : C.textSecondary,
                        }}>
                        {name}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

export default function AttachmentPreviewModal({ open, attachment, onClose, loader }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [source, setSource] = useState(null);   // { kind:"excel"|"sheetjs", wb, names }
    const [sheetIdx, setSheetIdx] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [zoom, setZoom] = useState(1);   // 스프레드시트 확대비율 (헤더에서 제어)

    const spreadsheet = isSpreadsheet(attachment);
    const img = isImage(attachment);
    const setZoomClamped = (z) => setZoom(Math.max(0.5, Math.min(2.5, Math.round(z * 10) / 10)));
    const zoomBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 1, cursor: "pointer", border: `1px solid ${C.border}`, color: C.textSecondary };
    const blobRef = useRef(null);   // 원본 blob 보관 → 화면 내 다운로드용

    const handleDownload = () => {
        const blob = blobRef.current;
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = attachment?.filename || "download";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
    };

    useEffect(() => {
        if (!open || !attachment) return;
        let cancelled = false;
        let objUrl = null;
        setLoading(true); setError(null); setBlobUrl(null); setSource(null); setSheetIdx(0); setZoom(1); blobRef.current = null;

        // 소스: loader(커스텀 프록시, 예: 채널 서버변환) > url(직접) > id(메일 프록시)
        const directUrl = !loader && attachment.url ? attachment.url : null;
        const loadBlob = loader
            ? () => Promise.resolve(loader()).then((r) => r?.data ?? r)
            : directUrl
                ? () => fetch(directUrl).then((r) => { if (!r.ok) throw new Error("fetch"); return r.blob(); })
                : () => mailApi.previewAttachment(attachment.id).then((r) => r?.data ?? r);
        const ext = (attachment.filename || "").split(".").pop().toLowerCase();
        const isOfficeDoc = ["doc", "docx", "ppt", "pptx", "odt", "odp", "rtf"].includes(ext);

        (async () => {
            try {
                if (isSpreadsheet(attachment)) {
                    const blob = await loadBlob();
                    if (cancelled) return;
                    blobRef.current = blob;   // 원본 보관(다운로드 버튼용)
                    const buf = await blob.arrayBuffer();
                    if (cancelled) return;
                    if (ext === "xlsx" || ext === "xlsm") {
                        try {
                            const wb = new ExcelJS.Workbook();
                            await wb.xlsx.load(buf);
                            if (cancelled) return;
                            setSource({ kind: "excel", wb, names: wb.worksheets.map((w) => w.name) });
                            return;
                        } catch { /* ExcelJS 런타임 실패 → SheetJS 폴백(값만) */ }
                    }
                    const wb = XLSX.read(buf, { type: "array", cellDates: true });
                    if (!cancelled) setSource({ kind: "sheetjs", wb, names: wb.SheetNames });
                    return;
                }
                if (directUrl) {
                    // 직접 URL 모드(채널): 이미지·PDF·텍스트는 URL 그대로, 오피스는 변환 서버 없어 미지원
                    if (img || (attachment.mimeType || "").includes("pdf") || (attachment.mimeType || "").startsWith("text/")
                        || /\.(pdf|txt|csv|log|md)$/i.test(attachment.filename || "")) {
                        if (!cancelled) setBlobUrl(directUrl);
                        return;
                    }
                    if (!cancelled) setError(isOfficeDoc ? "이 형식은 미리보기를 지원하지 않습니다." : "미리보기를 지원하지 않는 형식입니다.");
                    return;
                }
                // 프록시(메일) 모드: blob → objectURL (오피스는 서버가 PDF 변환본을 내려줌)
                const blob = await loadBlob();
                if (cancelled) return;
                objUrl = URL.createObjectURL(blob);
                setBlobUrl(objUrl);
            } catch {
                if (!cancelled) setError("미리보기를 불러오지 못했습니다.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, attachment?.id, attachment?.url, attachment?.filename]);

    const centered = { flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto" };
    // 여백 있는 본문 안에서 콘텐츠를 감싸는 카드(모서리 살짝·테두리·그림자) — 표/문서가 화면 끝에 딱 붙지 않게.
    const card = { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", borderRadius: 1.5, border: `1px solid ${C.border}`, overflow: "hidden", bgcolor: "#fff", boxShadow: "0 2px 14px rgba(15,23,42,0.07)" };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{ sx: { width: "92vw", height: "100vh", m: 0, borderRadius: 0, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative", bgcolor: spreadsheet ? "#eef2f6" : "#0f172a" } }}
        >
            {/* 헤더 — 엑셀에서만(파일명+다운로드+확대+닫기). 그 외(PDF·이미지·오피스)는 헤더 없이 콘텐츠만.
                닫기는 모달 밖 클릭 / Esc 로 처리. */}
            {spreadsheet && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, bgcolor: "#fff", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                    <InsertDriveFileRoundedIcon sx={{ fontSize: 18, color: C.textMuted, flexShrink: 0 }} />
                    <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, minWidth: 0 }}>
                        {attachment?.filename || "미리보기"}
                    </Typography>
                    <IconButton size="small" onClick={handleDownload} title="다운로드" sx={{ flexShrink: 0 }}>
                        <DownloadRoundedIcon sx={{ fontSize: 18, color: C.textSecondary }} />
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    {!loading && !error && source && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 0.5 }}>
                            <Box sx={zoomBtn} onClick={() => setZoomClamped(zoom - 0.1)} title="축소"><RemoveRoundedIcon sx={{ fontSize: 16 }} /></Box>
                            <Typography onClick={() => setZoom(1)} title="100%로" sx={{ fontSize: 12.5, fontWeight: 700, color: C.textSecondary, minWidth: 46, textAlign: "center", cursor: "pointer" }}>
                                {Math.round(zoom * 100)}%
                            </Typography>
                            <Box sx={zoomBtn} onClick={() => setZoomClamped(zoom + 0.1)} title="확대"><AddRoundedIcon sx={{ fontSize: 16 }} /></Box>
                        </Box>
                    )}
                    <IconButton size="small" onClick={onClose}><CloseRoundedIcon sx={{ fontSize: 20, color: C.textMuted }} /></IconButton>
                </Box>
            )}

            {/* 본문 — 엑셀만 여백+카드, 그 외(PDF·이미지·오피스)는 화면을 꽉 채움. */}
            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", ...(spreadsheet ? { p: 2 } : {}) }}>
                {loading ? (
                    <Box sx={centered}><CircularProgress size={28} /></Box>
                ) : error ? (
                    <Box sx={centered}>
                        <Box sx={{ textAlign: "center", p: 3 }}>
                            <Typography sx={{ ...ds.typography.body, color: spreadsheet ? C.textSecondary : "#e2e8f0", mb: 0.5 }}>{error}</Typography>
                            <Typography sx={{ fontSize: 12.5, color: spreadsheet ? C.textMuted : "#94a3b8" }}>
                                미리보기를 생성할 수 없는 파일입니다. 다운로드해 확인하세요.
                            </Typography>
                        </Box>
                    </Box>
                ) : spreadsheet ? (
                    source
                        ? <Box sx={card}><SpreadsheetView source={source} sheetIdx={sheetIdx} setSheetIdx={setSheetIdx} zoom={zoom} /></Box>
                        : <Box sx={centered}><CircularProgress size={28} /></Box>
                ) : blobUrl ? (
                    img ? (
                        <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <img src={blobUrl} alt={attachment?.filename || "preview"}
                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                        </Box>
                    ) : (
                        <iframe title="attachment-preview" src={blobUrl}
                            style={{ flex: 1, width: "100%", height: "100%", border: "none" }} />
                    )
                ) : null}
            </Box>
        </Dialog>
    );
}
